import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getPublicMedia } from '@/services/media.api';
import { FrontendMedia } from '@/services/transform';

const SOURCE_IMGS: Record<string, string> = {
  'Nature': '/images/media-logo-nature.jpg',
  'Science': '/images/media-logo-science.jpg',
  'MIT Technology Review': '/images/media-logo-mit.jpg',
  'Financial Times': '/images/media-logo-ft.jpg',
  'Reuters': '/images/media-logo-reuters.jpg',
  'The Economist': '/images/media-logo-economist.jpg',
};

function FeaturedCard({ item, isZh, visible, delay }: { item: FrontendMedia; isZh: boolean; visible: boolean; delay: number }) {
  const { t } = useTranslation();
  return (
    <a
      href={item.url}
      className={`group block bg-white rounded-2xl overflow-hidden border border-[#0A2540]/8 hover:-translate-y-1 transition-all duration-500 cursor-pointer ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
      rel="nofollow"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <span
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wider uppercase"
            style={{ backgroundColor: `${item.sourceLogoColor}15`, color: item.sourceLogoColor }}
          >
            {isZh ? item.category : item.categoryEn}
          </span>
          <span className="text-[#0A2540]/30 text-xs">{item.date}</span>
        </div>
        <h3 className="text-[#0A2540] font-bold text-base leading-snug mb-3 group-hover:text-[#2C7DA0] transition-colors line-clamp-3">
          {isZh ? item.title : item.titleEn}
        </h3>
        <p className="text-[#0A2540]/55 text-sm leading-relaxed line-clamp-3">
          {isZh ? item.excerpt : item.excerptEn}
        </p>
      </div>
      <div
        className="flex items-center justify-between px-6 py-4 border-t border-[#0A2540]/6"
        style={{ backgroundColor: `${item.sourceLogoColor}08` }}
      >
        <span className="font-bold text-sm" style={{ color: item.sourceLogoColor }}>
          {item.source}
        </span>
        <div className="flex items-center gap-1 text-xs font-semibold text-[#2C7DA0] group-hover:gap-2 transition-all">
          <span>{t('media.readMore')}</span>
          <i className="ri-external-link-line text-xs" />
        </div>
      </div>
    </a>
  );
}

function SmallCard({ item, isZh, visible, delay }: { item: FrontendMedia; isZh: boolean; visible: boolean; delay: number }) {
  const { t } = useTranslation();
  return (
    <a
      href={item.url}
      className={`group flex gap-4 bg-white rounded-2xl p-4 border border-[#0A2540]/8 hover:-translate-y-0.5 transition-all duration-500 cursor-pointer ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
      style={{ transitionDelay: `${delay}ms` }}
      rel="nofollow"
    >
      <div
        className="flex-shrink-0 w-1 rounded-full self-stretch"
        style={{ backgroundColor: item.sourceLogoColor }}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="font-bold text-xs" style={{ color: item.sourceLogoColor }}>
            {item.source}
          </span>
          <span className="text-[#0A2540]/25 text-xs">{item.date}</span>
        </div>
        <h4 className="text-[#0A2540] font-semibold text-sm leading-snug line-clamp-2 group-hover:text-[#2C7DA0] transition-colors mb-1">
          {isZh ? item.title : item.titleEn}
        </h4>
        <p className="text-[#0A2540]/45 text-xs leading-relaxed line-clamp-2 hidden sm:block">
          {isZh ? item.excerpt : item.excerptEn}
        </p>
      </div>
      <div className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-[#0A2540]/20 group-hover:text-[#2C7DA0] transition-colors self-center">
        <i className="ri-arrow-right-up-line text-sm" />
      </div>
    </a>
  );
}

function MediaApplyModal({ onClose, isZh }: { onClose: () => void; isZh: boolean }) {
  const [form, setForm] = useState({ orgName: '', contactName: '', email: '', mediaType: '', intro: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch('/api/public/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.contactName,
          email: form.email,
          organization: form.orgName,
          subject: isZh ? '媒体合作申请' : 'Media Partnership Application',
          message: `${isZh ? '媒体类型' : 'Media Type'}: ${form.mediaType}\n${isZh ? '合作需求' : 'Intent'}: ${form.intro}`,
        }),
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error('Submission failed');
      }
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const mediaTypes = isZh
    ? ['报纸/杂志', '网络媒体', '广播/电视', '学术期刊', '独立记者/博主', '其他']
    : ['Newspaper/Magazine', 'Online Media', 'Broadcast/TV', 'Academic Journal', 'Independent Journalist/Blogger', 'Other'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-r from-[#0A2540] to-[#1a3a5c]">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-7 h-7 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-sm" />
          </button>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-[#E8A838]/20">
              <i className="ri-newspaper-line text-[#E8A838] text-sm" />
            </div>
            <span className="text-[#E8A838] text-xs font-semibold uppercase tracking-widest">
              {isZh ? '媒体合作' : 'Media Partnership'}
            </span>
          </div>
          <h3 className="text-white font-bold text-xl mb-1">
            {isZh ? '申请媒体合作' : 'Apply for Media Partnership'}
          </h3>
          <p className="text-white/60 text-xs">
            {isZh ? '填写基本信息，我们将在2个工作日内与您联系' : 'We will contact you within 2 business days'}
          </p>
        </div>

        <div className="p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-emerald-100 mb-4">
                <i className="ri-checkbox-circle-fill text-3xl text-emerald-600" />
              </div>
              <h4 className="text-[#0A2540] font-bold text-base mb-2">
                {isZh ? '申请已提交！' : 'Application Submitted!'}
              </h4>
              <p className="text-[#0A2540]/60 text-sm">
                {isZh ? '感谢您的申请，我们将尽快与您联系。' : 'Thank you! We will get in touch with you shortly.'}
              </p>
              <button
                onClick={onClose}
                className="mt-5 px-6 py-2 bg-[#0A2540] text-white rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap"
              >
                {isZh ? '关闭' : 'Close'}
              </button>
            </div>
          ) : (
            <form

              id="media-partnership-apply"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#0A2540]/60 mb-1.5">
                    {isZh ? '媒体/机构名称 *' : 'Media / Organization *'}
                  </label>
                  <input
                    type="text"
                    name="orgName"
                    required
                    value={form.orgName}
                    onChange={(e) => setForm({ ...form, orgName: e.target.value })}
                    placeholder={isZh ? '媒体全称' : 'Full organization name'}
                    className="w-full px-3 py-2 border border-[#0A2540]/15 rounded-lg text-sm text-[#0A2540] focus:outline-none focus:border-[#0A2540] transition-colors placeholder-[#0A2540]/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#0A2540]/60 mb-1.5">
                    {isZh ? '联系人姓名 *' : 'Contact Name *'}
                  </label>
                  <input
                    type="text"
                    name="contactName"
                    required
                    value={form.contactName}
                    onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                    placeholder={isZh ? '记者/编辑姓名' : 'Journalist / Editor'}
                    className="w-full px-3 py-2 border border-[#0A2540]/15 rounded-lg text-sm text-[#0A2540] focus:outline-none focus:border-[#0A2540] transition-colors placeholder-[#0A2540]/30"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#0A2540]/60 mb-1.5">
                  {isZh ? '联系邮箱 *' : 'Contact Email *'}
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={isZh ? '请输入您的工作邮箱' : 'Enter your work email'}
                  className="w-full px-3 py-2 border border-[#0A2540]/15 rounded-lg text-sm text-[#0A2540] focus:outline-none focus:border-[#0A2540] transition-colors placeholder-[#0A2540]/30"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#0A2540]/60 mb-1.5">
                  {isZh ? '媒体类型 *' : 'Media Type *'}
                </label>
                <select
                  name="mediaType"
                  required
                  value={form.mediaType}
                  onChange={(e) => setForm({ ...form, mediaType: e.target.value })}
                  className="w-full px-3 py-2 border border-[#0A2540]/15 rounded-lg text-sm text-[#0A2540] focus:outline-none focus:border-[#0A2540] transition-colors bg-white cursor-pointer"
                >
                  <option value="">{isZh ? '请选择媒体类型' : 'Select media type'}</option>
                  {mediaTypes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#0A2540]/60 mb-1.5">
                  {isZh ? '合作需求说明' : 'Collaboration Intent'}
                </label>
                <textarea
                  name="intro"
                  rows={3}
                  maxLength={500}
                  value={form.intro}
                  onChange={(e) => setForm({ ...form, intro: e.target.value })}
                  placeholder={isZh ? '简述您希望获取的报道资源或合作形式（500字以内）' : 'Briefly describe what media resources or collaboration you need (max 500 characters)'}
                  className="w-full px-3 py-2 border border-[#0A2540]/15 rounded-lg text-sm text-[#0A2540] focus:outline-none focus:border-[#0A2540] transition-colors resize-none placeholder-[#0A2540]/30"
                />
                <div className="text-right text-xs text-[#0A2540]/30 mt-1">{form.intro.length}/500</div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {submitting
                  ? (isZh ? '提交中...' : 'Submitting...')
                  : (isZh ? '提交申请' : 'Submit Application')
                }
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MediaCoverage() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [liveMedia, setLiveMedia] = useState<FrontendMedia[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.05 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    loadMediaData();
  }, []);

  const loadMediaData = async () => {
    try {
      setLoading(true);
      const data = await getPublicMedia();
      setLiveMedia(data || []);
    } catch (error) {
      console.error('加载媒体报道失败:', error);
      setLiveMedia([]);
    } finally {
      setLoading(false);
    }
  };

  // Pinned/featured items shown first in featured section
  const featured = (liveMedia || []).filter((m) => m.featured);
  const rest = (liveMedia || []).filter((m) => !m.featured);

  // Media logos row for social proof
  const allSources = Array.from(new Set((liveMedia || []).map((m) => m.source)));

  return (
    <section className="py-16 md:py-24 bg-[#F7F9FC]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Section header */}
        <div
          className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A2540]/6 text-[#0A2540]/60 text-xs font-semibold mb-4 tracking-widest uppercase">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-newspaper-line" />
            </div>
            {t('media.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-3">
            {t('media.title')}
          </h2>
          <p className="text-[#0A2540]/55 text-base max-w-xl mx-auto">
            {t('media.subtitle')}
          </p>
        </div>

        {/* Media logos social proof strip */}
        <div
          className={`flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-12 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        >
          {allSources.map((source) => {
            const item = liveMedia.find((m) => m.source === source)!;
            return (
              <div key={source} className="flex items-center gap-1.5 opacity-40 hover:opacity-70 transition-opacity cursor-default">
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.sourceLogoColor }}
                />
                <span className="text-[#0A2540] font-bold text-sm whitespace-nowrap">{source}</span>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className="text-center py-12 text-[#9CA3AF]">
            <i className="ri-loader-4-line animate-spin text-2xl mb-2" />
            <p className="text-sm">{isZh ? '加载中...' : 'Loading...'}</p>
          </div>
        ) : (
          <>
            {/* Featured cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
              {featured.map((item, idx) => (
                <FeaturedCard
                  key={item.id}
                  item={item}
                  isZh={isZh}
                  visible={visible}
                  delay={idx * 80 + 200}
                />
              ))}
            </div>

            {/* Small cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
              {rest.map((item, idx) => (
                <SmallCard
                  key={item.id}
                  item={item}
                  isZh={isZh}
                  visible={visible}
                  delay={idx * 80 + 440}
                />
              ))}
            </div>
          </>
        )}

        {/* Media Partnership CTA */}
        <div
          className={`relative overflow-hidden rounded-2xl transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1a3a5c 50%, #0A2540 100%)' }}
        >
          {/* Decorative pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-[#E8A838] translate-x-1/3 -translate-y-1/3" />
            <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-[#2C7DA0] -translate-x-1/3 translate-y-1/3" />
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6 px-8 py-8 md:py-10">
            <div className="text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#E8A838]/15 text-[#E8A838] text-xs font-semibold mb-3 tracking-widest uppercase">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shake-hands-line" />
                </div>
                {isZh ? '媒体合作' : 'Media Partnership'}
              </div>
              <h3 className="text-white font-bold text-xl md:text-2xl mb-2">
                {isZh ? '记者或媒体机构？一键联系 GTRA' : 'Journalist or Media Outlet? Connect with GTRA'}
              </h3>
              <p className="text-white/55 text-sm max-w-lg">
                {isZh
                  ? '我们为媒体合作伙伴提供采访资质申请、新闻素材包、独家专访及活动现场媒体席位等资源，助力您的深度报道。'
                  : 'We offer media partners press credentials, press kits, exclusive interviews, and on-site media seats at GTRA events to support your in-depth coverage.'
                }
              </p>
              <div className="flex flex-wrap gap-4 mt-4 justify-center md:justify-start">
                {(isZh
                  ? ['采访资质申请', '新闻素材包', '专家采访对接', '活动媒体席位']
                  : ['Press Credentials', 'Press Kits', 'Expert Interview Access', 'Event Media Seats']
                ).map((item) => (
                  <div key={item} className="flex items-center gap-1.5 text-white/60 text-xs">
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-check-line text-[#E8A838]" />
                    </div>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={() => setShowApplyModal(true)}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl font-semibold text-sm transition-all cursor-pointer whitespace-nowrap hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #E8A838, #F5C842)', color: '#0A2540' }}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-edit-line" />
                </div>
                {isZh ? '申请媒体合作' : 'Apply for Media Access'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showApplyModal && (
        <MediaApplyModal onClose={() => setShowApplyModal(false)} isZh={isZh} />
      )}
    </section>
  );
}
